import { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { api } from '../../api';
import { CharacterListResponse } from '../../api/types';
import { ScenarioCharacterCard } from './ScenarioCharacterCard';
import { useDevice } from '../../context/DeviceContext';
import styles from './EditScenarioCharactersSearch.module.css';

type SearchTab = 'trending' | 'my-characters' | 'search';

interface EditScenarioCharactersSearchProps {
  onSelectCharacter: (character: CharacterListResponse) => void;
  onClose?: () => void;
  isVisible: boolean;
  existingCharacterIds?: string[];
  isUpdating?: boolean;
}

function EditScenarioCharactersSearch({ 
  onSelectCharacter, 
  onClose, 
  isVisible,
  existingCharacterIds = [],
  isUpdating = false
}: EditScenarioCharactersSearchProps): JSX.Element | null {
  const { isMobile } = useDevice();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<CharacterListResponse[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState<SearchTab>('trending');
  const initialLoadRef = useRef(true);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedCharacterId, setSelectedCharacterId] = useState<string | null>(null);
  const prevExistingIdsRef = useRef<string[]>([]);
  const prevTabBeforeSearchRef = useRef<SearchTab>('trending');

  // Memoize the existing character IDs set for efficient lookups
  const existingCharacterIdsSet = useMemo(() => {
    return new Set(existingCharacterIds);
  }, [existingCharacterIds]);

  // Only reload results when existingCharacterIds actually change in a meaningful way
  useEffect(() => {
    // Only update if we're visible and it's not the initial load
    if (isVisible && !initialLoadRef.current) {
      // Check if the list has meaningfully changed
      const prevIds = new Set(prevExistingIdsRef.current);
      const currentIds = new Set(existingCharacterIds);
      
      // Check if there's a real difference (character added or removed)
      let hasChanged = false;
      if (prevIds.size !== currentIds.size) {
        hasChanged = true;
      } else {
        // Check if the specific IDs are different
        for (const id of prevIds) {
          if (!currentIds.has(id)) {
            hasChanged = true;
            break;
          }
        }
      }
      
      // Only reload if there was a meaningful change
      if (hasChanged) {
        // Determine which tab to reload
        if (activeTab === 'trending') {
          loadTrendingCharacters(false); // Don't flash loading indicator
        } else if (activeTab === 'my-characters') {
          loadUserCharacters(false); // Don't flash loading indicator
        } else if (searchQuery.trim().length > 0) {
          performSearch(searchQuery, false); // Don't flash loading indicator
        }
      }
    }
    
    // Update reference of previous IDs
    prevExistingIdsRef.current = [...existingCharacterIds];
  }, [existingCharacterIds, isVisible, activeTab, searchQuery]);

  // Load trending characters on initial mount
  useEffect(() => {
    if (isVisible && initialLoadRef.current) {
      loadTrendingCharacters();
      initialLoadRef.current = false;
    }
  }, [isVisible]);

  // Focus search input when expanded
  useEffect(() => {
    if (isVisible && !isCollapsed && !isMobile && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isVisible, isCollapsed, isMobile]);

  // Load appropriate characters when tab changes
  useEffect(() => {
    if (!isVisible) return;
    
    if (activeTab === 'trending') {
      loadTrendingCharacters();
    } else if (activeTab === 'my-characters') {
      loadUserCharacters();
    }
    
    // Store the active tab as previous if it's not the search tab
    if (activeTab !== 'search') {
      prevTabBeforeSearchRef.current = activeTab;
    }
  }, [activeTab, isVisible]);

  // Search for characters when the search query changes
  useEffect(() => {
    if (!isVisible) return;
    
    const searchCharacters = async () => {
      if (searchQuery.trim().length === 0) {
        // If search is empty and we're in search tab, return to previous tab
        if (activeTab === 'search') {
          setActiveTab(prevTabBeforeSearchRef.current);
        } else {
          // Otherwise just refresh the current tab
          if (activeTab === 'trending') {
            loadTrendingCharacters();
          } else if (activeTab === 'my-characters') {
            loadUserCharacters();
          }
        }
        return;
      }

      // Save the current tab before switching to search
      if (activeTab !== 'search') {
        prevTabBeforeSearchRef.current = activeTab;
      }
      
      // Auto-expand when searching
      setIsCollapsed(false);
      // Switch to search tab when searching
      setActiveTab('search');
      
      performSearch(searchQuery);
    };

    // Debounce search to avoid too many API calls
    const debounceTimeout = setTimeout(searchCharacters, 300);
    return () => clearTimeout(debounceTimeout);
  }, [searchQuery, isVisible, activeTab]);

  // Perform search with loading state control
  const performSearch = async (query: string, showLoading = true) => {
    if (showLoading) {
      setIsSearching(true);
    }
    
    try {
      const results = await api.characters.search(query);
      // Filter out characters that are already in the scenario
      const filteredResults = filterExistingCharacters(results);
      
      // Use functional update to prevent React batching issues
      setSearchResults(prevResults => {
        // If the results are essentially the same, don't update state to prevent rerenders
        if (areSearchResultsEqual(prevResults, filteredResults)) {
          return prevResults;
        }
        return filteredResults;
      });
    } catch (error) {
      console.error('Error searching for characters:', error);
    } finally {
      if (showLoading) {
        setIsSearching(false);
      }
    }
  };

  // Compare two arrays of search results to see if they're essentially the same
  const areSearchResultsEqual = (prevResults: CharacterListResponse[], newResults: CharacterListResponse[]): boolean => {
    if (prevResults.length !== newResults.length) return false;
    
    // Create maps for faster lookup
    const prevMap = new Map(prevResults.map(char => [char.id, char]));
    
    // Check if all items in newResults are in prevResults
    for (const char of newResults) {
      if (!prevMap.has(char.id)) return false;
    }
    
    return true;
  };

  // Filter out characters that are already in the scenario
  const filterExistingCharacters = (characters: CharacterListResponse[]): CharacterListResponse[] => {
    if (existingCharacterIdsSet.size === 0) {
      return characters;
    }
    
    return characters.filter(character => !existingCharacterIdsSet.has(character.id));
  };

  const loadTrendingCharacters = async (showLoading = true) => {
    if (showLoading) {
      setIsSearching(true);
    }
    
    try {
      const trending = await api.characters.filter("trending");
      // Filter out characters that are already in the scenario
      const filteredResults = filterExistingCharacters(trending);
      
      // Use functional update to prevent React batching issues
      setSearchResults(prevResults => {
        // If the results are essentially the same, don't update state to prevent rerenders
        if (areSearchResultsEqual(prevResults, filteredResults)) {
          return prevResults;
        }
        return filteredResults;
      });
    } catch (error) {
      console.error('Error loading trending characters:', error);
    } finally {
      if (showLoading) {
        setIsSearching(false);
      }
    }
  };

  const loadUserCharacters = async (showLoading = true) => {
    if (showLoading) {
      setIsSearching(true);
    }
    
    try {
      // Use the owned() method to get characters owned by the current user
      const userCharacters = await api.characters.owned();
      // Filter out characters that are already in the scenario
      const filteredResults = filterExistingCharacters(userCharacters);
      
      // Use functional update to prevent React batching issues
      setSearchResults(prevResults => {
        // If the results are essentially the same, don't update state to prevent rerenders
        if (areSearchResultsEqual(prevResults, filteredResults)) {
          return prevResults;
        }
        return filteredResults;
      });
    } catch (error) {
      console.error('Error loading user characters:', error);
      // Fallback to empty array if the API call fails
      setSearchResults([]);
    } finally {
      if (showLoading) {
        setIsSearching(false);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleCloseSearch = () => {
    if (onClose) {
      onClose();
    }
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleSelectCharacter = (character: CharacterListResponse) => {
    // Don't allow selection if we're already updating
    if (isUpdating) return;
    
    // Set the selected character ID to trigger the animation
    setSelectedCharacterId(character.id);
    
    // Call the onSelectCharacter callback immediately
    onSelectCharacter(character);
    
    // Reset the selected character ID after a delay
    setTimeout(() => {
      setSelectedCharacterId(null);
    }, 300);
    
    // Clear search query after selection on desktop
    if (!isMobile) {
      setSearchQuery('');
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const handleTabChange = (tab: SearchTab) => {
    setActiveTab(tab);
    setSearchQuery('');
  };

  // Convert CharacterListResponse to ScenarioCharacter format for display
  const convertToScenarioCharacter = (character: CharacterListResponse) => {
    return {
      id: character.id,
      display_name: character.display_name,
      tagline: character.tagline,
      icon: character.icon,
      username: character.username || character.display_name.toLowerCase().replace(/\s+/g, '_'),
      is_mature: false
    };
  };

  const getTabTitle = () => {
    if (searchQuery) return 'SEARCH RESULTS';
    switch (activeTab) {
      case 'trending': return 'TRENDING CHARACTERS';
      case 'my-characters': return 'MY CHARACTERS';
      default: return 'CHARACTERS';
    }
  };

  // Helper function to determine if a character is being selected
  const getCharacterItemClass = (character: CharacterListResponse): string => {
    if (character.id === selectedCharacterId) {
      return styles.characterBeingSelected;
    }
    return '';
  };

  if (!isVisible) return null;

  // Render mobile search overlay
  if (isMobile) {
    return ReactDOM.createPortal(
      <div className={styles.mobileSearchOverlay}>
        <div className={styles.mobileSearchHeader}>
          <button 
            className={styles.mobileBackButton}
            onClick={handleCloseSearch}
            aria-label="Close search"
          >
            ‹
          </button>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Search for characters..."
              value={searchQuery}
              onChange={handleSearchChange}
              className={styles.mobileSearchInput}
              autoFocus
            />
            {searchQuery && (
              <button 
                onClick={handleClearSearch}
                style={{
                  position: 'absolute',
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: 'rgba(255, 255, 255, 0.6)',
                  cursor: 'pointer',
                  fontSize: '18px',
                  padding: '5px'
                }}
                aria-label="Clear search"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <div className={styles.mobileTabs}>
          <button 
            className={`${styles.mobileTab} ${activeTab === 'trending' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('trending')}
          >
            Trending
          </button>
          <button 
            className={`${styles.mobileTab} ${activeTab === 'my-characters' ? styles.activeTab : ''}`}
            onClick={() => handleTabChange('my-characters')}
          >
            My Characters
          </button>
        </div>

        <div className={styles.mobileSearchResults}>
          {isSearching ? (
            <div className={styles.loadingState}>Loading characters...</div>
          ) : searchResults.length === 0 ? (
            <div className={styles.emptyState}>
              <p>No characters found. Try a different search term.</p>
            </div>
          ) : (
            <>
              <h3 className={styles.resultsTitle}>{getTabTitle()}</h3>
              <div className={styles.resultsGrid}>
                {searchResults.map((character) => (
                  <div 
                    key={character.id} 
                    className={`${styles.searchResultItem} ${getCharacterItemClass(character)}`}
                  >
                    <ScenarioCharacterCard
                      character={convertToScenarioCharacter(character)}
                      showAddButton={true}
                      onAddClick={() => handleSelectCharacter(character)}
                      isSearchResult={true}
                    />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  // Render desktop search panel
  return (
    <div className={`${styles.searchContainer} ${isCollapsed ? styles.searchContainerCollapsed : ''}`}>
      <div className={styles.searchHeader} onClick={toggleCollapse}>
        <input
          type="text"
          placeholder="Search for characters..."
          value={searchQuery}
          onChange={handleSearchChange}
          className={styles.searchInput}
          onClick={(e) => e.stopPropagation()}
          ref={searchInputRef}
        />
      </div>
      
      {!isCollapsed && (
        <>
          <div className={styles.tabsContainer}>
            <button 
              className={`${styles.tabButton} ${activeTab === 'trending' ? styles.activeTabButton : ''}`}
              onClick={() => handleTabChange('trending')}
            >
              Trending
            </button>
            <button 
              className={`${styles.tabButton} ${activeTab === 'my-characters' ? styles.activeTabButton : ''}`}
              onClick={() => handleTabChange('my-characters')}
            >
              My Characters
            </button>
          </div>
          
          <div className={styles.searchResults}>
            {isSearching ? (
              <div className={styles.loadingState}>Loading characters...</div>
            ) : searchResults.length === 0 ? (
              <div className={styles.emptyState}>
                <p>No characters found. Try a different search term.</p>
              </div>
            ) : (
              <>
                <h3 className={styles.resultsTitle}>{getTabTitle()}</h3>
                <div className={styles.resultsGrid}>
                  {searchResults.map((character) => (
                    <div 
                      key={character.id} 
                      className={`${styles.searchResultItem} ${getCharacterItemClass(character)}`}
                    >
                      <ScenarioCharacterCard
                        character={convertToScenarioCharacter(character)}
                        showAddButton={true}
                        onAddClick={() => handleSelectCharacter(character)}
                        isSearchResult={true}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
      {isUpdating && (
        <div className={styles.updatingOverlay}>
          <span>Adding character...</span>
        </div>
      )}
    </div>
  );
}

export default EditScenarioCharactersSearch; 